import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChefHat, Moon, Sun } from 'lucide-react';

const Landing = ({ affiliateUtils }) => {
  const [darkMode, setDarkMode] = useState(false);

  // Check for saved dark mode preference or system preference
  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode');
    const systemPreference = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedMode !== null) {
      setDarkMode(savedMode === 'true');
    } else {
      setDarkMode(systemPreference);
    }
  }, []);

  // Save dark mode preference
  useEffect(() => {
    localStorage.setItem('darkMode', darkMode.toString());
  }, [darkMode]);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode 
    ? 'bg-gradient-to-t from-[#888888] to-[#000000]' 
    : 'bg-gradient-to-br from-orange-50 to-red-50'
}`}>
      {/* Header */}
      <meta name='impact-site-verification' value='261ebbb5-54a6-4ee9-9b61-83171a428aaf'></meta>
      <header className="container mx-auto px-6 py-8">
        <nav className="flex justify-between items-center">
          <div className="flex items-center justify-center mb-4">
            <img 
    src={darkMode ? "dark-logo2-800x300.png" : "/light-800x300px.png"} 
    alt="Fridge to Fork Logo" 
    className="h-48 w-auto transition-all duration-300 ease-in-out" // 96px tall - adjust as needed
  />
</div>
          
          <div className="flex items-center gap-4">
            {/* Dark Mode Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-full transition-colors ${
                darkMode 
                  ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              } shadow-lg`}
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* Header CTA Button */}
            <Link 
              to="/app" 
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                darkMode 
                  ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                  : 'bg-orange-600 hover:bg-orange-700 text-white'
              }`}
              onClick={() => {
                if (affiliateUtils) {
                  affiliateUtils.trackLandingCTA('header', 'try_it_free');
                }
              }}
              
            >  
              <meta name='impact-site-verification' value='261ebbb5-54a6-4ee9-9b61-83171a428aaf'></meta> 
              Try It Free
            </Link>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <main className="container mx-auto px-6">
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h1 className={`text-5xl md:text-6xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Turn Your 
            <span className={`${darkMode ? 'text-orange-400' : 'text-orange-600'}`}> Leftover Ingredients </span>
            Into Amazing Recipes
          </h1>
          
          <p className={`text-xl mb-8 max-w-2xl mx-auto ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Stop staring at your fridge wondering what to cook. Our smart recipe generator finds delicious meals using ingredients you already have!
          </p>

          {/* Main Hero CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* Primary CTA - Links to Recipe Generator */}
            <Link 
              to="/app" 
              className={`px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center justify-center gap-2 transition-colors ${
                darkMode 
                  ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                  : 'bg-orange-600 hover:bg-orange-700 text-white'
              }`}
              onClick={() => {
                if (affiliateUtils) {
                  affiliateUtils.trackLandingCTA('hero', 'lets_cook');
                }
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Let's Cook!
            </Link>
            
            {/* Secondary CTA - Scroll to Nutritional Biohacking newsletter*/}
            <a 
              href="#signup" 
              className={`border-2 px-8 py-4 rounded-xl font-semibold text-lg transition-colors ${
                darkMode 
                  ? 'border-orange-400 text-orange-400 hover:bg-orange-400 hover:text-gray-900' 
                  : 'border-orange-600 text-orange-600 hover:bg-orange-50'
              }`}
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'landing_scroll', {
                    'section': 'signup',
                    'action': 'newsletter_signup'
                  });
                }
              }}
            >
              Coming Soon! - Sign Up - Nutritional Biohacking Newsletter
            </a>
          </div>

          {/* Social Proof */}
          <div className="mt-12 text-center">
            <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Trusted by home chefs worldwide
            </p>
            <div className={`flex justify-center items-center gap-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                </svg>
                <span className="font-medium">4.9/5 Rating</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                </svg>
                <span className="font-medium">10,000+ Recipes Created</span>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <section id="how-it-works" className="mb-20">
          <h2 className={`text-4xl font-bold text-center mb-12 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            How It Works
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                darkMode ? 'bg-orange-900' : 'bg-orange-100'
              }`}>
                <svg className={`w-8 h-8 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
              </div>
              <h3 className={`text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                1. Add Your Ingredients
              </h3>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                Simply type in what you have in your fridge, pantry, or kitchen. No need to measure or be precise.
              </p>
            </div>

            <div className="text-center p-6">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                darkMode ? 'bg-orange-900' : 'bg-orange-100'
              }`}>
                <svg className={`w-8 h-8 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 className={`text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                2. Get Smart Matches
              </h3>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                Our algorithm instantly finds recipes you can make, ranked by how many ingredients you already have.
              </p>
            </div>

            <div className="text-center p-6">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                darkMode ? 'bg-orange-900' : 'bg-orange-100'
              }`}>
                <ChefHat className={`w-8 h-8 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} />
              </div>
              <h3 className={`text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                3. Start Cooking!
              </h3>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                Get step-by-step instructions with exact measurements. Missing ingredients? We'll help you get them delivered.
              </p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className={`mb-20 rounded-3xl p-12 shadow-lg ${
          darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'
        }`}>
          <h2 className={`text-4xl font-bold text-center mb-12 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Why Use Fridge to Fork?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className={`text-2xl font-semibold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Save Money & Reduce Waste
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                  </svg>
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                    Avoid unnecessary grocery trips
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                  </svg>
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                    Use ingredients before they expire
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                  </svg>
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                    Reduce food waste by 40%
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className={`text-2xl font-semibold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Smart & Convenient
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                  </svg>
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                    Instant recipe matching algorithm
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                  </svg>
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                    Get missing ingredients delivered
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                  </svg>
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                    Works on all devices, no download required - Bookmark us!
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={`text-center rounded-3xl p-12 text-white mb-20 ${
          darkMode 
            ? 'bg-gradient-to-br from-orange-600 to-red-600' 
            : 'bg-gradient-to-br from-orange-600 to-red-600'
        }`}>
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Cooking?</h2>
          <p className="text-xl mb-8 opacity-90">Join thousands of home chefs who never run out of meal ideas</p>
          
          <Link 
            to="/app" 
            className="bg-white text-orange-600 px-8 py-4 rounded-xl hover:bg-gray-100 transition-colors font-semibold text-lg inline-flex items-center gap-2"
            onClick={() => {
              if (affiliateUtils) {
                affiliateUtils.trackLandingCTA('bottom_cta', 'start_cooking_free');
              }
            }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Start Cooking for Free
          </Link>
          
          <p className="text-sm mt-4 opacity-75">No signup required • Works instantly • Always free</p>
        </section>
      </main>

      {/* Footer */}
      <footer className={`py-12 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-900 text-white'}`}>
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand Column */}
            {/* Brand Column */}
<div>
  <div className="flex items-center mb-4">
    <img 
        src="/landingpg-footer-dark-800x300.png" 
      alt="Fridge to Fork Logo" 
      className="h-18 w-auto"
    />
  </div>
  <p className="text-gray-400">Transform your leftover ingredients into delicious recipes instantly</p>
</div>

            {/* Product Column */}
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/app" className="hover:text-white transition-colors">
                    Recipe Generator
                  </Link>
                </li>
                <li>
                  <a href="#how-it-works" className="hover:text-white transition-colors">
                    How It Works
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal Column */}
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a 
                    href="/terms.html" 
                    className="hover:text-white transition-colors"
                    onClick={() => {
                      if (window.gtag) {
                        window.gtag('event', 'footer_link_click', {
                          'link_name': 'terms_conditions',
                          'link_destination': 'terms_html'
                        });
                      }
                    }}
                  >
                    Terms & Conditions
                  </a>
                </li>
                <li>
                  <a 
                    href="/privacy.html" 
                    className="hover:text-white transition-colors"
                    onClick={() => {
                      if (window.gtag) {
                        window.gtag('event', 'footer_link_click', {
                          'link_name': 'privacy_policy',
                          'link_destination': 'privacy_html'
                        });
                      }
                    }}
                  >
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a 
                    href="/about.html" 
                    className="hover:text-white transition-colors"
                    onClick={() => {
                      if (window.gtag) {
                        window.gtag('event', 'footer_link_click', {
                          'link_name': 'about',
                          'link_destination': 'about_html'
                        });
                      }
                    }}
                  >
                    About
                  </a>
                </li>
                <li>
                  <a 
                    href="mailto:hello@fridgetofork.com" 
                    className="hover:text-white transition-colors"
                    onClick={() => {
                      if (window.gtag) {
                        window.gtag('event', 'contact_click', {
                          'contact_method': 'email',
                          'source': 'footer'
                        });
                      }
                    }}
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">© 2025 Fridge to Fork. All rights reserved.</p>
            <p className="text-gray-400 text-sm mt-2 sm:mt-0">Made with ❤️ for home chefs</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
            